import React, { useContext, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Megaphone, Loader2 } from 'lucide-react';

/**
 * BroadcastDialog — landlords/employees message all active tenants at once.
 *
 * Renders its own trigger button; the parent just drops it into the header.
 */
export function BroadcastDialog() {
  const { token } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    const trimmed = content.trim();
    if (trimmed.length < 3) {
      toast.error('Please write at least a few words.');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/messages/broadcast`,
        { content: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const sent = res.data?.sent ?? 0;
      if (sent === 0) {
        toast.info(res.data?.detail || 'No active tenants found to notify.');
      } else {
        toast.success(`Announcement sent to ${sent} tenant${sent === 1 ? '' : 's'}.`);
        setContent('');
        setOpen(false);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Broadcast failed.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        data-testid="broadcast-open-btn"
        onClick={() => setOpen(true)}
        variant="outline"
        size="sm"
        className="rounded-full gap-2"
        title="Send an announcement to all active tenants"
      >
        <Megaphone className="w-4 h-4" />
        <span className="hidden sm:inline">Broadcast</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" /> Announcement to active tenants
            </DialogTitle>
            <DialogDescription>
              Sends a one-way message to every tenant with an active lease under your account.
              They'll receive it in Messages + a notification.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="broadcast-content"
            placeholder="e.g. Quarterly inspections next week. We'll email specifics 48h in advance."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={2000}
            rows={6}
          />
          <div className="text-xs text-muted-foreground text-right">{content.length} / 2000</div>
          <DialogFooter>
            <Button data-testid="broadcast-cancel-btn" variant="outline" onClick={() => setOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button data-testid="broadcast-send-btn" onClick={send} disabled={sending || content.trim().length < 3}>
              {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send announcement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
